// Navmesh pathfinding wrapper (polygon + funnel). The agent radius is enforced by
// our own "valid center" test rather than the library's meshShrinkAmount (which, in
// this version, does NOT exclude near-wall points). A point is a valid actor center
// if it is on the walkmesh AND not within `radius` of a REAL wall — portal seams
// shared between adjacent polygons are not walls, so rooms stay connected.
// See research/DECISIONS.md, Decision 2.
import { closestOnSegment, dist, nearestOnPolys, pointInAny, type Polygon, type Vec2 } from "../core/geometry";
import type { Room } from "../core/schema";

import * as NavMeshPkg from "navmesh";
const NavMesh: any = (NavMeshPkg as any).NavMesh ?? (NavMeshPkg as any).default ?? NavMeshPkg;

export class Nav {
  private mesh: any;
  private polys: Polygon[];
  private radius: number;
  /** Real wall segments (boundary of the walkable region; portal seams excluded). */
  private walls: [Vec2, Vec2][];

  constructor(room: Room, extraAreas: Polygon[] = []) {
    // base floor + any currently-active gated areas (drawbridge down, gate open, ...)
    this.polys = (room.walkmesh as Polygon[]).concat(extraAreas);
    this.radius = room.actorRadius;
    this.mesh = new NavMesh(this.polys.map((p) => p.map((pt) => ({ x: pt.x, y: pt.y }))));
    this.walls = this.buildWalls();
  }

  /** Classify every polygon edge into wall vs portal sub-segments, ONCE. An edge can be
   * partly a wall and partly a portal (e.g. the band edge that borders both a neighbour
   * band AND an obstacle hole), so we subdivide and probe each sub-interval: if the side
   * opposite the polygon interior is open (off-mesh) it's a wall; if a neighbour covers
   * it, it's a portal seam. Contiguous wall sub-intervals coalesce into one segment. */
  private buildWalls(): [Vec2, Vec2][] {
    const out: [Vec2, Vec2][] = [];
    const eps = 0.75;
    for (const poly of this.polys) {
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[j]!;
        const b = poly[i]!;
        const len = Math.hypot(b.x - a.x, b.y - a.y);
        if (len < 1e-6) continue;
        const dx = (b.x - a.x) / len;
        const dy = (b.y - a.y) / len;
        const nx = -dy; // unit normal
        const ny = dx;
        const N = Math.max(1, Math.ceil(len / 2)); // ~2px resolution
        let runStart = -1;
        const flush = (endIdx: number) => {
          if (runStart < 0) return;
          const t0 = runStart / N;
          const t1 = endIdx / N;
          out.push([
            { x: a.x + (b.x - a.x) * t0, y: a.y + (b.y - a.y) * t0 },
            { x: a.x + (b.x - a.x) * t1, y: a.y + (b.y - a.y) * t1 },
          ]);
          runStart = -1;
        };
        for (let k = 0; k < N; k++) {
          const t = (k + 0.5) / N;
          const mx = a.x + (b.x - a.x) * t;
          const my = a.y + (b.y - a.y) * t;
          const inPlus = pointInAny({ x: mx + nx * eps, y: my + ny * eps }, this.polys);
          const inMinus = pointInAny({ x: mx - nx * eps, y: my - ny * eps }, this.polys);
          const isWall = inPlus !== inMinus; // one side interior, the other open => wall
          if (isWall && runStart < 0) runStart = k;
          else if (!isWall) flush(k);
        }
        flush(N);
      }
    }
    return out;
  }

  /** Distance from p to the nearest real wall (segment distance includes endpoints, so
   * reflex/obstacle corners are handled). Infinity if the room has no walls. */
  private wallDist(p: Vec2): number {
    let best = Infinity;
    for (const [a, b] of this.walls) {
      const c = closestOnSegment(p, a, b);
      const d = dist(p, c);
      if (d < best) best = d;
    }
    return best;
  }

  /** On the walkable ground (unshrunk)? Used to decide "was the click off-area". */
  isWalkable(p: Vec2): boolean {
    return pointInAny(p, this.polys);
  }

  /** Keep a live position on the walkable ground (no wall-clearance — that lives in
   * the inset PATH waypoints). Cheap; only does work when actually off-mesh. */
  ground(p: Vec2): Vec2 {
    return pointInAny(p, this.polys) ? p : nearestOnPolys(p, this.polys);
  }

  /** Valid actor-center position: on the mesh and clear of every real wall (and corner)
   * by the agent radius. Cheap — just distance to the precomputed wall segments. */
  validCenter(p: Vec2): boolean {
    return pointInAny(p, this.polys) && this.wallDist(p) >= this.radius;
  }

  /** Clamp any point to the nearest valid actor-center (expanding-ring search). Anchors
   * the search at the nearest mesh point so even far-off clicks resolve to an INTERIOR
   * valid center (clear of walls by the agent radius), not a boundary point. */
  clamp(p: Vec2): Vec2 {
    if (this.validCenter(p)) return p;
    // a far-off click: search from its nearest point on the mesh, not from p itself,
    // so the ring actually reaches the (possibly distant) floor.
    const seed = pointInAny(p, this.polys) ? p : nearestOnPolys(p, this.polys);
    if (this.validCenter(seed)) return seed;
    const ANGLES = 24;
    for (let r = 2; r <= 160; r += 3) {
      let best: Vec2 | null = null;
      let bestD = Infinity;
      for (let i = 0; i < ANGLES; i++) {
        const a = (i / ANGLES) * Math.PI * 2;
        const cand = { x: seed.x + Math.cos(a) * r, y: seed.y + Math.sin(a) * r };
        if (this.validCenter(cand)) {
          const d = (p.x - cand.x) ** 2 + (p.y - cand.y) ** 2; // nearest to the original click
          if (d < bestD) {
            bestD = d;
            best = cand;
          }
        }
      }
      if (best) return best;
    }
    return seed; // degenerate room (narrower than the agent) — at least on the mesh
  }

  // ---- Navigation v2: start/goal-aware routing -------------------------------------
  // The bundled `navmesh` library routes its polygon A* by CENTROID distance only, so it
  // ignores where the real start/goal sit inside their polygons. Around a symmetric
  // obstacle both ways cost the same and it picks one blindly (the "long way round" bug).
  // We keep the library's mesh (it builds neighbours + oriented portals correctly) but
  // run our OWN A* threaded through portal midpoints from the real start to the real
  // goal, then smooth with the Simple Stupid Funnel. See research/DECISIONS.md #2.

  /** A* over the nav polygons, cost = real distance through portal midpoints. Returns the
   * polygon sequence start->goal, or null if disconnected. */
  private routePolys(start: Vec2, goal: Vec2): any[] | null {
    const polys: any[] = this.mesh.getPolygons();
    const startPoly = polys.find((p) => p.contains({ x: start.x, y: start.y }));
    const goalPoly = polys.find((p) => p.contains({ x: goal.x, y: goal.y }));
    if (!startPoly || !goalPoly) return null;
    if (startPoly === goalPoly) return [startPoly];

    const g = new Map<number, number>(); // best dist-from-start to a poly's entry point
    const entry = new Map<number, Vec2>(); // the portal midpoint we entered a poly through
    const from = new Map<number, any>();
    g.set(startPoly.id, 0);
    entry.set(startPoly.id, { x: start.x, y: start.y });
    const open: any[] = [startPoly];

    while (open.length) {
      // pop lowest f = g + straight-line(entry, goal) — small graphs, linear scan is fine
      let bi = 0;
      let bf = Infinity;
      for (let i = 0; i < open.length; i++) {
        const n = open[i]!;
        const f = g.get(n.id)! + dist(entry.get(n.id)!, goal);
        if (f < bf) {
          bf = f;
          bi = i;
        }
      }
      const u = open.splice(bi, 1)[0]!;
      if (u.id === goalPoly.id) break;
      const ep = entry.get(u.id)!;
      const gu = g.get(u.id)!;
      for (let k = 0; k < u.neighbors.length; k++) {
        const v = u.neighbors[k]!;
        const portal = u.portals[k]!;
        const mid = { x: (portal.start.x + portal.end.x) / 2, y: (portal.start.y + portal.end.y) / 2 };
        const tentative = gu + dist(ep, mid);
        if (tentative < (g.get(v.id) ?? Infinity)) {
          g.set(v.id, tentative);
          entry.set(v.id, mid);
          from.set(v.id, u);
          if (!open.includes(v)) open.push(v);
        }
      }
    }

    if (!from.has(goalPoly.id) && startPoly.id !== goalPoly.id) return null;
    const seq: any[] = [goalPoly];
    let cur = goalPoly;
    while (cur.id !== startPoly.id) {
      cur = from.get(cur.id);
      if (!cur) return null;
      seq.unshift(cur);
    }
    return seq;
  }

  /** Simple Stupid Funnel (Mononen) over the channel of (left,right) portal points. */
  private funnel(channel: { left: Vec2; right: Vec2 }[]): Vec2[] {
    const tri = (a: Vec2, b: Vec2, c: Vec2) => (b.x - a.x) * (c.y - a.y) - (c.x - a.x) * (b.y - a.y);
    const eq = (a: Vec2, b: Vec2) => a.x === b.x && a.y === b.y;
    const pts: Vec2[] = [];
    let apex = channel[0]!.left;
    let left = channel[0]!.left;
    let right = channel[0]!.right;
    let ai = 0;
    let li = 0;
    let ri = 0;
    pts.push({ x: apex.x, y: apex.y });
    for (let i = 1; i < channel.length; i++) {
      const l = channel[i]!.left;
      const r = channel[i]!.right;
      // tighten the right side
      if (tri(apex, right, r) <= 0) {
        if (eq(apex, right) || tri(apex, left, r) > 0) {
          right = r;
          ri = i;
        } else {
          pts.push({ x: left.x, y: left.y });
          apex = left;
          ai = li;
          left = apex;
          right = apex;
          li = ai;
          ri = ai;
          i = ai;
          continue;
        }
      }
      // tighten the left side
      if (tri(apex, left, l) >= 0) {
        if (eq(apex, left) || tri(apex, right, l) < 0) {
          left = l;
          li = i;
        } else {
          pts.push({ x: right.x, y: right.y });
          apex = right;
          ai = ri;
          left = apex;
          right = apex;
          li = ai;
          ri = ai;
          i = ai;
          continue;
        }
      }
    }
    const last = channel[channel.length - 1]!.left;
    if (!pts.length || !eq(pts[pts.length - 1]!, last)) pts.push({ x: last.x, y: last.y });
    return pts;
  }

  /** Build the funnel channel (start, each crossed portal as left/right, goal) for a
   * polygon sequence. The library orients each portal relative to its OWN polygon's
   * centroid, which is not globally consistent across a multi-hop path — so we re-orient
   * every portal's left/right by the actual TRAVEL direction (u.centroid -> v.centroid),
   * which is what the funnel requires. */
  private buildChannel(seq: any[], start: Vec2, goal: Vec2): { left: Vec2; right: Vec2 }[] {
    const ch: { left: Vec2; right: Vec2 }[] = [{ left: start, right: start }];
    for (let i = 0; i < seq.length - 1; i++) {
      const u = seq[i]!;
      const v = seq[i + 1]!;
      let portal: any = null;
      for (let k = 0; k < u.neighbors.length; k++) if (u.neighbors[k]!.id === v.id) portal = u.portals[k]!;
      if (!portal) continue;
      const A = { x: portal.start.x, y: portal.start.y };
      const B = { x: portal.end.x, y: portal.end.y };
      const dx = v.centroid.x - u.centroid.x;
      const dy = v.centroid.y - u.centroid.y;
      // cross(dir, A-u.centroid): in screen coords (y down) this is >0 when A is to the
      // RIGHT of travel; so A is LEFT when the cross product is <= 0.
      const sideA = dx * (A.y - u.centroid.y) - dy * (A.x - u.centroid.x);
      if (sideA <= 0) ch.push({ left: A, right: B });
      else ch.push({ left: B, right: A });
    }
    ch.push({ left: goal, right: goal });
    return ch;
  }

  /** Clamp a goal into the connected component reachable from `start`. Used when the raw
   * goal sits in a disconnected region (e.g. across an open chasm with the bridge up) so
   * the hero walks to the reachable EDGE instead of straight through the void. */
  private reachableGoal(start: Vec2, goalRaw: Vec2): Vec2 {
    const polys: any[] = this.mesh.getPolygons();
    const sp = polys.find((p) => p.contains({ x: start.x, y: start.y }));
    if (!sp) return this.clamp(goalRaw);
    const seen = new Set<number>([sp.id]);
    const stack = [sp];
    while (stack.length) {
      const u = stack.pop()!;
      for (const v of u.neighbors) if (!seen.has(v.id)) { seen.add(v.id); stack.push(v); }
    }
    // already reachable? keep it
    if (polys.some((p) => seen.has(p.id) && p.contains({ x: goalRaw.x, y: goalRaw.y }))) return this.clamp(goalRaw);
    const reachPts: Polygon[] = polys
      .filter((p) => seen.has(p.id))
      .map((p) => p.getPoints().map((pt: any) => ({ x: pt.x, y: pt.y })));
    return this.clamp(nearestOnPolys(goalRaw, reachPts));
  }

  /** Path from start to a (clamped) goal. Always returns at least [goal]. */
  findPath(start: Vec2, goalRaw: Vec2): { goal: Vec2; clamped: boolean; path: Vec2[] } {
    let clamped = !this.isWalkable(goalRaw);
    const s = this.validCenter(start) ? start : this.clamp(start);
    let goal = this.clamp(goalRaw);

    let seq = this.routePolys(s, goal);
    if (!seq) {
      // goal is in a different connected component — clamp it into the reachable one
      goal = this.reachableGoal(s, goalRaw);
      clamped = true;
      seq = this.routePolys(s, goal);
    }
    let path: Vec2[];
    if (!seq) {
      path = [goal]; // still give the player a destination
    } else if (seq.length === 1) {
      path = [s, goal]; // same polygon: straight line
    } else {
      path = this.funnel(this.buildChannel(seq, s, goal));
    }
    // inset every waypoint so the path itself stays clear of walls, then pin the goal.
    path = path.map((pt) => this.clamp({ x: pt.x, y: pt.y }));
    path[path.length - 1] = goal;
    return { goal, clamped, path };
  }
}
